/**
 * 🏢 Company Admin Enterprise Consumption Dashboard — Vague 4 P1
 * ===============================================================
 *
 * Vue RH/Admin entreprise : consommation globale des forfaits,
 * collaborateurs à relancer, échéances proches.
 *
 * Source : GET /api/company/admin/consumption-dashboard
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/environment';
import BackButton from '../components/BackButton';
import { Loader2, TrendingUp, AlertTriangle, Clock, Users, Building2 } from 'lucide-react';

const API = getApiUrl();

function formatDateFR(iso) {
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
}

function StatCard({ icon: Icon, label, value, highlight }) {
  return (
    <div className={`lt-card p-5 ${highlight ? 'border-[#D4AF37] bg-gradient-to-br from-[#FEF3C7] to-[#FBF9F3]' : ''}`}
      data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2.5 mb-2">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
          highlight ? 'bg-[#92710A] text-white' : 'bg-[#F3F1EB] text-[#92710A]'
        }`}>
          <Icon className="w-4.5 h-4.5" />
        </div>
        <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold">{label}</div>
      </div>
      <div className={`font-display text-3xl font-semibold ${highlight ? 'text-[#92710A]' : 'text-[#111827]'}`}>
        {value}
      </div>
    </div>
  );
}

export default function CompanyEnterpriseConsumption() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedOrderId, setExpandedOrderId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/company/admin/consumption-dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { setData(null); return; }
      setData(await res.json());
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="theme-light min-h-[50vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#92710A]" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="theme-light">
        <div className="max-w-3xl mx-auto px-4 pt-24 pb-[max(6rem,env(safe-area-inset-bottom))]">
          <BackButton />
          <div className="mt-6 lt-card p-8 text-center" data-testid="rh-no-access">
            <AlertTriangle className="w-10 h-10 mx-auto text-[#991B1B] mb-3" />
            <h1 className="font-display text-2xl text-[#111827] mb-2">Accès refusé</h1>
            <p className="text-[14px] text-[#4B5563]">
              Cette page est réservée aux administrateurs entreprise.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { summary, orders } = data;

  return (
    <div className="theme-light" data-testid="company-consumption-dashboard">
      <div className="max-w-6xl mx-auto px-4 pt-20 sm:pt-24 pb-[max(6rem,env(safe-area-inset-bottom))] sm:pb-16">
        <BackButton />

        <motion.header initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="mt-6 mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#111827] text-white text-[11px] font-semibold uppercase tracking-wider mb-3">
            <Building2 className="w-3.5 h-3.5" /> Pilotage RH
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-[#111827] leading-tight">
            Consommation des forfaits entreprise
          </h1>
          <p className="text-[14px] text-[#4B5563] mt-2">
            Vue synthétique de l’utilisation, des collaborateurs à relancer et des échéances proches.
          </p>
        </motion.header>

        {/* Stats */}
        <section className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10" data-testid="rh-stats-grid">
          <StatCard icon={Users} label="Forfaits actifs" value={summary.total_orders_active} />
          <StatCard icon={TrendingUp} label="Taux d’utilisation"
            value={`${Math.round((summary.utilization_rate || 0) * 100)}%`} />
          <StatCard icon={AlertTriangle} label="À relancer"
            value={summary.collabs_to_relaunch}
            highlight={summary.collabs_to_relaunch > 0} />
          <StatCard icon={Clock} label="Échéances ≤ 7j"
            value={summary.orders_near_expiry}
            highlight={summary.orders_near_expiry > 0} />
        </section>

        {/* Orders détaillés */}
        <section data-testid="rh-orders-list">
          <h2 className="font-display text-xl text-[#111827] mb-4">Détail par commande</h2>
          {orders.length === 0 ? (
            <div className="lt-card p-8 text-center text-[13px] text-[#6B7280]" data-testid="rh-orders-empty">
              Aucune commande entreprise active pour le moment.
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map((o) => {
                const isExpanded = expandedOrderId === o.order_id;
                const utPct = Math.round((o.utilization_rate || 0) * 100);
                const barColor = utPct >= 80 ? 'bg-[#991B1B]' : utPct >= 50 ? 'bg-[#D97706]' : 'bg-[#047857]';
                return (
                  <article key={o.order_id}
                    className="lt-card p-5"
                    data-testid={`rh-order-${o.order_id}`}>
                    <button
                      className="w-full text-left"
                      onClick={() => setExpandedOrderId(isExpanded ? null : o.order_id)}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <h3 className="font-display text-[17px] text-[#111827]">
                            {o.company_name || 'Entreprise'}
                          </h3>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-[#F3F1EB] text-[#4B5563] border border-[#E8DFC8]">
                              {o.type === 'ia' ? 'IA' : 'Humain'}
                            </span>
                            <span className="text-[11px] text-[#6B7280]">
                              {o.eligible_coach_count} coach{o.eligible_coach_count > 1 ? 's' : ''} validé{o.eligible_coach_count > 1 ? 's' : ''}
                            </span>
                            {o.is_near_expiry && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FEE2E2] text-[#991B1B] border border-[#FECACA] inline-flex items-center gap-1 font-semibold animate-pulse"
                                data-testid={`rh-order-expiring-${o.order_id}`}>
                                <Clock className="w-3 h-3" /> J-{o.days_until_expiry}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-display text-2xl text-[#111827] font-semibold">
                            {o.sessions_used}<span className="lt-muted text-sm"> / {o.sessions_allocated_to_collabs}</span>
                          </div>
                          <div className="text-[11px] text-[#6B7280]">{utPct}% utilisé</div>
                        </div>
                      </div>
                      <div className="w-full h-1.5 bg-[#F3F1EB] rounded-full overflow-hidden mt-3">
                        <div className={`h-full ${barColor} transition-all duration-500`}
                          style={{ width: `${utPct}%` }} />
                      </div>
                      <div className="flex items-center justify-between mt-2 text-[12px] text-[#6B7280]">
                        <span>Valable jusqu’au {o.valid_until ? formatDateFR(o.valid_until) : '—'}</span>
                        <span className="font-medium text-[#92710A]">{isExpanded ? 'Masquer' : 'Voir les collaborateurs'}</span>
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t border-[#E8E5DE] space-y-2"
                        data-testid={`rh-order-collabs-${o.order_id}`}>
                        {o.collaborators.length === 0 ? (
                          <p className="text-[12px] text-[#6B7280] italic">Aucun collaborateur rattaché.</p>
                        ) : o.collaborators.map(c => (
                          <div key={c.collaborator_id}
                            className={`flex items-center justify-between gap-3 p-2.5 rounded-lg ${
                              c.to_relaunch ? 'bg-[#FEF3C7] border border-[#FDE68A]' : 'bg-[#FBF9F3]'
                            }`}
                            data-testid={`rh-collab-${c.collaborator_id}`}>
                            <div className="min-w-0 flex-1">
                              <div className="text-[13px] font-medium text-[#111827] truncate">{c.email}</div>
                              <div className="text-[11px] text-[#6B7280]">
                                {c.sessions_used}/{c.sessions_allocated} sessions · {Math.round(c.utilization_rate * 100)}%
                                {c.status === 'invited' && <span className="ml-2 text-[#92710A]">· invité</span>}
                              </div>
                            </div>
                            <div className="flex gap-1.5 flex-wrap justify-end">
                              {c.to_relaunch && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#92400E] text-white font-semibold">
                                  À relancer
                                </span>
                              )}
                              {c.near_quota && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#991B1B] text-white font-semibold">
                                  Quota critique
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
